import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Drawer,
	AppBar,
	Toolbar,
	Typography,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListItemButton,
	Divider,
	IconButton,
	InputBase,
	Paper,
	Grid,
	Card,
	CardContent,
	Avatar,
	Badge,
	LinearProgress,
	Slide,
	Dialog,
} from '@mui/material';

import { alpha, styled } from '@mui/material/styles';
import {
	Menu as MenuIcon,
	Search as SearchIcon,
	DashboardOutlined as DashboardIcon,
	InventoryOutlined as InventoryIcon,
	AssessmentOutlined as ReportsIcon,
	ShoppingCartCheckoutOutlined as OrdersIcon,
	SettingsOutlined as SettingsIcon,
	NotificationsOutlined as NotificationsOutlinedIcon,
	ChevronLeft as ChevronLeftIcon,
	TrendingUpOutlined as TrendingUpIcon,
	LocalPharmacyOutlined as LocalPharmacyIcon,
	Inventory2Outlined as Inventory2OutlinedIcon,
} from '@mui/icons-material';
import logo from './images/logoImage.jpeg';
// Import tab components
import Dashboard from './tabs/Dashboard';
import InventoryTab from './tabs/InventoryTab';
import Reports from './tabs/Reports';
import Orders from './tabs/Orders';
import RestClient from '../../../api/axiosInterceptor';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from '../../../contexts/ThemeContext'; // Adjust path as needed
import { useTheme } from '@mui/material/styles'; // Material UI theme hook
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import ThemeToggler from '../../../scenes/global/ThemeToggler';
import { useMediaQuery } from '@mui/material';

const drawerWidth = 240;

const Search = styled('div')(({ theme }) => ({
	position: 'relative',
	borderRadius: theme.shape.borderRadius,
	backgroundColor: alpha(theme.palette.common.white, 0.15),
	'&:hover': {
		backgroundColor: alpha(theme.palette.common.white, 0.25),
	},
	marginRight: theme.spacing(2),
	marginLeft: 0,
	width: '100%',
	[theme.breakpoints.up('sm')]: {
		marginLeft: theme.spacing(3),
		width: 'auto',
	},
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
	padding: theme.spacing(0, 2),
	height: '100%',
	position: 'absolute',
	pointerEvents: 'none',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
	color: 'inherit',
	'& .MuiInputBase-input': {
		padding: theme.spacing(1, 1, 1, 0),
		paddingLeft: `calc(1em + ${theme.spacing(4)})`,
		transition: theme.transitions.create('width'),
		width: '100%',
		[theme.breakpoints.up('md')]: {
			width: '20ch',
		},
	},
}));

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="down" ref={ref} {...props} />;
});

const Inventory = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [searchDialogOpen, setSearchDialogOpen] = useState(false);
	const [drugs, setDrugs] = useState([]);
	const [expiringDrugs, setExpiringDrugs] = useState([]);
	const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
	const [selectedDrugFromNotification, setSelectedDrugFromNotification] =
		useState(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [open, setOpen] = useState(true);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [searchExpanded, setSearchExpanded] = useState(false);

	// Access custom theme context
	const { mode, toggleTheme } = useContext(ThemeContext);

	// Function to cycle through themes on button click
	const cycleTheme = () => {
		if (mode === 'light') toggleTheme('dark');
		else if (mode === 'dark') toggleTheme('system');
		else toggleTheme('light');
	};

	// Get icon based on current theme
	const getThemeIcon = () => {
		switch (mode) {
			case 'light':
				return <LightModeIcon />;
			case 'dark':
				return <DarkModeIcon />;
			default:
				return <SettingsBrightnessIcon />;
		}
	};

	useEffect(() => {
		// Hide header, footer and top banner
		const header = document.getElementById('app-header');
		const footer = document.getElementById('app-footer');
		const topBanner = document.getElementById('top-banner');

		if (header) header.style.display = 'none';
		if (footer) footer.style.display = 'none';
		if (topBanner) topBanner.style.display = 'none';

		// Cleanup function to show elements when unmounting
		return () => {
			if (header) header.style.display = '';
			if (footer) footer.style.display = '';
			if (topBanner) topBanner.style.display = '';
		};
	}, []);

	const handleDrawerToggle = () => {
		setOpen(!open);
	};

	// Add handler for search input
	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const handleOpenSearchDialog = () => {
		setSearchDialogOpen(true);
	};

	const handleCloseSearchDialog = () => {
		setSearchDialogOpen(false);
	};

	const [activeMenu, setActiveMenu] = useState(() => {
		return localStorage.getItem('activeMenu') || 'dashboard';
	});

	// Menu item data to keep things DRY
	const menuItems = [
		{ id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
		{ id: 'inventory', label: 'Inventory', icon: InventoryIcon },
		{ id: 'reports', label: 'Reports', icon: ReportsIcon },
		{ id: 'orders', label: 'Orders', icon: OrdersIcon },
	];

	// Update the menu selection handler
	const handleMenuSelect = (menuId) => {
		setActiveMenu(menuId);
		localStorage.setItem('activeMenu', menuId);
		if (isMobile) {
			setMobileOpen(false);
		}
	};

	// Function to render the active component
	const renderActiveComponent = () => {
		switch (activeMenu) {
			case 'dashboard':
				return <Dashboard />;
			case 'inventory':
				return (
					<InventoryTab
						searchTerm={searchTerm}
						onExpiringDrugsFound={setExpiringDrugs}
						selectedDrug={selectedDrugFromNotification}
						onDrugDetailsClosed={() => setSelectedDrugFromNotification(null)}
					/>
				);
			case 'reports':
				return <Reports />;
			case 'orders':
				return <Orders searchTerm={searchTerm} />;
			case 'settings':
				return <Settings />;
			default:
				return <Dashboard />;
		}
	};

	// Get dynamic placeholder text based on active component
	const getPlaceholderText = () => {
		switch (activeMenu) {
			case 'inventory':
				return 'Search products...';
			case 'orders':
				return 'Search orders...';
			default:
				return 'Search...';
		}
	};

	// Fetch drugs when component mounts
	useEffect(() => {
		fetchDrugs();
	}, []);

	// Function to fetch drugs and filter expiring ones
	const fetchDrugs = async () => {
		try {
			const response = await RestClient.get('/medication/drug/');
			const drugsData = response.data;

			// Set all drugs first
			setDrugs(drugsData);

			// Filter drugs with expiration alerts
			const expiring = drugsData.filter((drug) => drug.expiration_alert);

			setExpiringDrugs(expiring);
		} catch (error) {
			console.error('Error fetching drugs:', error);
		}
	};

	// Handle notification menu
	const handleNotificationClick = (event) => {
		setNotificationAnchorEl(event.currentTarget);
	};

	const handleNotificationClose = () => {
		setNotificationAnchorEl(null);
	};

	return (
		<Box sx={{ display: 'flex', height: '100vh' }}>
			{/* Side Navigation */}
			<Drawer
				variant={isMobile ? 'temporary' : 'permanent'}
				open={isMobile ? mobileOpen : open}
				onClose={() => setMobileOpen(false)}
				sx={{
					width: open ? drawerWidth : 72,
					flexShrink: 0,
					display: { xs: 'block', sm: 'block' },
					'& .MuiDrawer-paper': {
						width: isMobile ? drawerWidth : open ? drawerWidth : 72,
						boxSizing: 'border-box',
						backgroundColor:
							theme.palette.customColors?.drawerBackground || '#FFFFFF',
						transition: theme.transitions.create(
							['width', 'background-color', 'color'],
							{
								easing: theme.transitions.easing.sharp,
								duration: theme.transitions.duration.enteringScreen,
							},
						),
						overflowX: 'hidden',
						color: theme.palette.customColors?.drawerText || '#2C3E50',
					},
				}}
			>
				<Link
					to="/"
					style={{
						textDecoration: 'none',
						display: 'block',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
							padding: '16px 0 8px',
							marginTop: '8px',
							marginBottom: '0',
							cursor: 'pointer',
						}}
					>
						<Avatar
							src={logo}
							alt="Company Logo"
							sx={{
								width: 40,
								height: 40,
								marginRight: 2,
							}}
						/>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 'bold',
								color: '#1570EF',
								fontSize: '1.1rem',
								letterSpacing: '0.5px',
							}}
						>
							M-TREAT
						</Typography>
					</Box>
				</Link>
				<Box
					sx={{
						overflow: 'auto',
						mt: 2,
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					<List>
						{menuItems.map((item) => {
							const Icon = item.icon;
							const isActive = activeMenu === item.id;

							return (
								<ListItemButton
									key={item.id}
									onClick={() => handleMenuSelect(item.id)}
									sx={{
										backgroundColor: 'transparent',
										borderLeft: isActive
											? `3px solid ${theme.palette.customColors?.activeMenuBorder || '#1570EF'}`
											: '3px solid transparent',
										paddingLeft: isActive ? '13px' : '16px',
										transition: theme.transitions.create(
											['border-color', 'padding'],
											{
												duration: theme.transitions.duration.standard,
											},
										),
									}}
								>
									<ListItemIcon>
										<Icon
											sx={{
												color: isActive
													? theme.palette.customColors?.brandColor || '#1570EF'
													: theme.palette.customColors?.menuText || '#2C3E50',
												transition: theme.transitions.create('color', {
													duration: theme.transitions.duration.standard,
												}),
											}}
										/>
									</ListItemIcon>
									{open && (
										<ListItemText
											primary={item.label}
											primaryTypographyProps={{
												fontWeight: isActive ? 'bold' : 'regular',
												color: isActive
													? theme.palette.customColors?.brandColor || '#1570EF'
													: theme.palette.customColors?.menuText || '#2C3E50',
												transition: theme.transitions.create('color', {
													duration: theme.transitions.duration.standard,
												}),
											}}
										/>
									)}
								</ListItemButton>
							);
						})}
					</List>

					<Divider />
				</Box>
			</Drawer>

			{/* mobile view */}
			{isMobile && (
				<AppBar
					position="fixed"
					sx={{
						display: { xs: 'block', sm: 'block', md: 'none' },
						backgroundColor: '#FFFFFF',
						color: '#1570EF',
						boxShadow: 1,
					}}
				>
					<Toolbar>
						<IconButton
							color="inherit"
							aria-label="open drawer"
							edge="start"
							onClick={() => setMobileOpen(!mobileOpen)}
							sx={{ mr: 2 }}
						>
							<MenuIcon />
						</IconButton>
						<Typography variant="h6" noWrap component="div">
							M-TREAT
						</Typography>
					</Toolbar>
				</AppBar>
			)}

			<Box
				sx={{
					flexGrow: 1,
					width: `calc(100% - ${open ? drawerWidth : 72}px)`,
					transition: theme.transitions.create('width', {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.enteringScreen,
					}),
					backgroundColor: 'transparent',
				}}
			>
				{/* App Bar */}
				<AppBar
					position="fixed"
					elevation={0}
					sx={{
						width: {
							xs: '100%',
							md: `calc(100% - ${isMobile ? 0 : open ? drawerWidth : 72}px)`,
						},
						ml: { xs: 0, md: isMobile ? 0 : open ? drawerWidth : 72 },
						backgroundColor:
							theme.palette.customColors?.drawerBackground || '#FFFFFF',
						border: {
							xs: 'none',
							sm: `4px solid ${theme.palette.customColors?.borderColor || '#F0F1F3'}`,
						},
						color: theme.palette.customColors?.drawerText || '#2C3E50',
						transition: theme.transitions.create(
							['width', 'background-color', 'color'],
							{
								easing: theme.transitions.easing.sharp,
								duration: theme.transitions.duration.enteringScreen,
							},
						),
						boxShadow:
							theme.palette.mode === 'dark'
								? '0 2px 10px rgba(0,0,0,0.2)'
								: '0 2px 10px rgba(0,0,0,0.05)',
						zIndex: (theme) => theme.zIndex.drawer - 1,
					}}
				>
					<Toolbar
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							minHeight: { xs: '56px', sm: '64px' },
							px: { xs: 1, sm: 2 },
						}}
					>
						{/* Menu toggle for mobile */}
						{isMobile && (
							<IconButton
								color="inherit"
								aria-label="open drawer"
								edge="start"
								onClick={() => setMobileOpen(!mobileOpen)}
								sx={{ mr: 1 }}
							>
								<MenuIcon />
							</IconButton>
						)}

						{/* Search */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								mb: 2,
								mt: 2,
								px: 2,
							}}
						>
							<Box
								sx={{
									display: { xs: 'none', sm: 'flex' },
									alignItems: 'center',
									backgroundColor:
										theme.palette.customColors?.searchBackground || '#ffffff',
									border: `1px solid ${theme.palette.customColors?.searchBorder || '#F0F1F3'}`,
									borderRadius: '4px',
									px: 2,
									py: 0.5,
									width: { xs: '100%', sm: '200px', md: '300px' },
									transition:
										'background-color 0.3s ease, border-color 0.3s ease',
								}}
							>
								<SearchIcon
									sx={{
										color: theme.palette.customColors?.drawerText || '#2C3E50',
										mr: 1,
										opacity: 0.6,
									}}
								/>
								<InputBase
									placeholder={getPlaceholderText()}
									inputProps={{ 'aria-label': 'search' }}
									sx={{
										flex: 1,
										color: theme.palette.customColors?.drawerText || '#2C3E50',
										fontSize: '0.9rem',
										transition: 'color 0.3s ease',
									}}
									value={searchTerm}
									onChange={handleSearchChange}
								/>
							</Box>

							{/* Search icon only on small screens */}
							<IconButton
								sx={{
									display: { xs: 'flex', sm: 'none' },
									color: theme.palette.customColors?.drawerText || '#2C3E50',
								}}
								aria-label="search"
								onClick={handleOpenSearchDialog}
							>
								<SearchIcon />
							</IconButton>

							<Dialog
								open={searchDialogOpen}
								onClose={handleCloseSearchDialog}
								TransitionComponent={Transition}
								fullWidth
								maxWidth="sm"
								BackdropProps={{
									style: { backgroundColor: 'transparent' },
								}}
								onBackdropClick={handleCloseSearchDialog}
								PaperProps={{
									sx: {
										position: 'absolute',
										top: 0,
										m: 0,
										borderTopLeftRadius: 0,
										borderTopRightRadius: 0,
										borderBottomLeftRadius: '16px',
										borderBottomRightRadius: '16px',
										maxHeight: '40%',
										width: '100%',
										boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
										backgroundColor: theme.palette.background.paper,
									},
								}}
								sx={{
									'& .MuiDialog-container': {
										alignItems: 'flex-start',
									},
									'& .MuiBackdrop-root': {
										backgroundColor: 'transparent',
									},
								}}
							>
								<Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											backgroundColor:
												theme.palette.mode === 'dark' ? '#333333' : '#f5f5f5',
											borderRadius: '4px',
											px: 2,
											py: 1,
											width: '100%',
										}}
									>
										<SearchIcon
											sx={{
												color:
													theme.palette.customColors?.drawerText || '#2C3E50',
												mr: 1,
												opacity: 0.6,
											}}
										/>
										<InputBase
											placeholder={getPlaceholderText()}
											autoFocus
											inputProps={{ 'aria-label': 'search' }}
											sx={{
												flex: 1,
												color:
													theme.palette.customColors?.drawerText || '#2C3E50',
												fontSize: '1rem',
											}}
											value={searchTerm}
											onChange={handleSearchChange}
										/>
										<IconButton
											color="inherit"
											onClick={handleCloseSearchDialog}
											sx={{
												color:
													theme.palette.customColors?.drawerText || '#2C3E50',
												ml: 1,
											}}
										>
											<CloseIcon />
										</IconButton>
									</Box>
								</Box>
							</Dialog>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{/* Notifications */}
							<Tooltip
								title={
									expiringDrugs.length > 0
										? `${expiringDrugs.length} drug${expiringDrugs.length !== 1 ? 's' : ''} expiring soon`
										: 'No expiring drugs'
								}
							>
								<IconButton
									size="medium"
									sx={{ ml: { xs: 0.5, sm: 1 } }}
									onClick={handleNotificationClick}
								>
									<Badge
										badgeContent={expiringDrugs.length || null}
										color="error"
										componentsProps={{
											badge: {
												sx: {
													'@keyframes blink': {
														'0%': { opacity: 1 },
														'50%': { opacity: 0 },
														'100%': { opacity: 1 },
													},
													animation: expiringDrugs.length
														? 'blink 1s infinite'
														: 'none',
												},
											},
										}}
									>
										<NotificationsOutlinedIcon
											sx={{
												color:
													theme.palette.customColors?.drawerText || '#2C3E50',
											}}
										/>
									</Badge>
								</IconButton>
							</Tooltip>
							{/* Notification dropdown menu */}
							<Menu
								anchorEl={notificationAnchorEl}
								open={Boolean(notificationAnchorEl)}
								onClose={handleNotificationClose}
								PaperProps={{
									elevation: 3,
									sx: {
										width: 320,
										maxHeight: 400,
										backgroundColor:
											theme.palette.mode === 'dark'
												? theme.palette.customColors?.paperBackground
												: '#f5f5f5',
									},
								}}
							>
								{expiringDrugs.length === 0 ? (
									<MenuItem>
										<ListItemText primary="No drugs expiring soon" />
									</MenuItem>
								) : (
									expiringDrugs.map((drug) => (
										<MenuItem
											key={drug.id}
											onClick={() => {
												// Close notification dropdown
												handleNotificationClose();

												// Set active menu to inventory tab
												setActiveMenu('inventory');

												// Set the selected drug to be passed to InventoryTab
												setSelectedDrugFromNotification(drug);
											}}
										>
											<ListItemIcon>
												<WarningIcon fontSize="small" color="error" />
											</ListItemIcon>
											<ListItemText
												primary={drug.name}
												secondary={
													<>
														<span>
															Expires on{' '}
															{new Date(
																drug.expiration_date,
															).toLocaleDateString()}
														</span>
														<br />
														<span
															style={{
																fontWeight: 'bold',
																color: drug.expiration_alert.includes('urgent')
																	? theme.palette.mode === 'dark'
																		? '#ffab40'
																		: '#ed6c02'
																	: theme.palette.mode === 'dark'
																		? '#f48fb1'
																		: '#d32f2f',
															}}
														>
															{drug.expiration_alert}
														</span>
													</>
												}
											/>
										</MenuItem>
									))
								)}
							</Menu>
							{/* Profile and Theme Toggler */}
							<Box
								sx={{
									ml: { xs: 1, sm: 2 },
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								{/* Profile Avatar */}
								<Avatar
									sx={{
										width: { xs: 32, sm: 36 },
										height: { xs: 32, sm: 36 },
										bgcolor:
											theme.palette.customColors?.brandColor || '#1570EF',
										cursor: 'pointer',
									}}
								>
									M
								</Avatar>

								{/* Theme Toggler */}
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<ThemeToggler />
								</Box>
							</Box>
						</Box>
					</Toolbar>
				</AppBar>
				<Toolbar
					sx={{
						minHeight: { xs: '72px', sm: '80px' },
					}}
				/>

				{/* Main content */}
				<Box
					component="main"
					sx={{
						flexGrow: 1,
						p: 0.5,
						backgroundColor:
							theme.palette.mode === 'dark'
								? theme.palette.customColors?.borderColor || '#2D3748'
								: '#f5f7fa',
						height: {
							xs: 'calc(100vh - 56px)',
							sm: 'calc(100vh - 64px)',
						},
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						transition: theme.transitions.create('background-color', {
							duration: theme.transitions.duration.standard,
						}),
					}}
				>
					<Box
						sx={{
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						}}
					>
						{renderActiveComponent()}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default Inventory;
